"use client";

/**
 * /alerts/new - create (or, with ?edit={id}, edit) a saved-search alert.
 *
 * FREE for any logged-in user (Stream 2). The primary path to creating
 * an alert is the "Create alert" action on the browse page; this manual
 * form is the fallback + the edit surface (the alerts-list pencil links
 * here with ?edit={id}).
 *
 * Anonymous visitors get the FULL builder (filters, live estimate) -
 * the account ask happens only at save: the draft is encoded as a
 * ?prefill= blob and carried through login via return_to, so the form
 * lands populated and ready to save after signup. Build-before-signup:
 * by the time we ask for an email the visitor has already invested in
 * (and can see the value of) the alert they configured.
 */
import { polygonParamsToRings, ringsToPolygonParams } from "@/lib/draw-area";
import { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams, type ReadonlyURLSearchParams } from "next/navigation";
import dynamic from "next/dynamic";
import Link from "@/lib/localized-link";
import { useAuth } from "@/lib/auth-context";
import VerifyEmailGate from "@/components/VerifyEmailGate";
import { useLocale, useT } from "@/lib/locale-context";
import { haptic } from "@/lib/haptics";
import { alertBlobToBrowseParams as blobToBrowseParams, browseParamsToAlertFilters, decodePrefill, encodePrefill } from "@/lib/filter-presets";
import { FREE_FREQUENCIES as FREE_ALERT_FREQUENCIES, OFFERED_FREQUENCIES } from "@/lib/alert-options";
import { kindChipKey, kindsOf } from "@/lib/property-kinds";
import MagicSearchBar from "@/components/listings/MagicSearchBar";
import { useFeatureFlag } from "@/lib/features";
import { hasProFeatures } from "@/lib/pro";
import AnimatedLogoMark from "@/components/brand/AnimatedLogoMark";
import SharedHeader from "@/components/SharedHeader";
import ListingsLocationPicker from "@/components/listings/ListingsLocationPicker";
import { API_BASE, getFeatureTags, type FeatureTagVocab } from "@/lib/api";
import { groupDigits, parseGrouped } from "@/lib/number-input";
import { trackEvent } from "@/lib/tracking";
import {
  isPushEligible, isThisDeviceSubscribed, subscribeToPush, unsubscribeFromPush,
  getOrCreatePushSubscription,
} from "@/lib/push";
import {
  isAppPushRegistered, isNativeApp, registerAppPush, unregisterAppPush,
} from "@/lib/native-app";
import { Bell, BellRing, ChevronLeft, ChevronDown, MapPin, Map as MapIcon, Pencil, Send, Check, Zap, Clock, AlertTriangle, Loader2, SlidersHorizontal, X, Info, TextSearch, ListFilter } from "lucide-react";
import { PANEL_CARRIED_KEYS, PANEL_PARAM_KEYS } from "@/lib/filter-panel-keys";
import { FILTER_COUNT, PRO_BADGE, SELECTED, UNSELECTED, rangeFill } from "@/lib/ui-classes";

// The listings page's own filter sheet: loaded on the first tap only.
const ListingsFilterPanel = dynamic(() => import("@/components/listings/ListingsFilterPanel"), { ssr: false });

// mapbox-gl touches ``window`` at import time. Lazy-load with
// ssr:false. ``@turf/circle`` generates the metric radius polygon
// since Mapbox ``circle`` paint is pixel-radius, not metric.
const LeafletRadiusPicker = dynamic(
  () => import("@/components/LeafletRadiusPickerMapbox"),
  { ssr: false, loading: () => (
    <div className="h-72 rounded-lg border border-dashed border-gray-200 bg-gray-50 animate-pulse" />
  ) },
);
// The drawn-area picker (third geo tab) shares the same rule.
const AreaDrawPicker = dynamic(
  () => import("@/components/alerts/AreaDrawPicker"),
  { ssr: false, loading: () => (
    <div className="h-72 rounded-lg border border-dashed border-gray-200 bg-gray-50 animate-pulse" />
  ) },
);

type GeoMode = "area" | "radius" | "draw";
type Tier = "any" | "good_deal" | "great_deal" | "exceptional_deal";
// Mirrors the backend's _VALID_FREQUENCIES - the legacy "30m"/"6h"
// stay in the TYPE so editing an old alert doesn't silently coerce it,
// but they're no longer offered (FREQUENCY_OPTIONS below).
type NotifyFrequency = "instant" | "30m" | "1h" | "6h" | "24h" | "3d" | "7d";
const NOTIFY_FREQUENCIES: readonly NotifyFrequency[] =
  ["instant", "30m", "1h", "6h", "24h", "3d", "7d"];
type Tristate = "any" | "yes" | "no";

interface FormState {
  roomCounts: number[];
  listingKind: string[]; // subset of ["new","old","house"]
  priceMin: number | "";
  priceMax: number | "";
  areaMin: number | "";
  areaMax: number | "";
  // Minimum gross rental yield, kept as a string (the raw input value)
  // and parsed into a number only when the filters blob is built. The
  // number of floors is the listings filter sheet's, one control for it
  // everywhere (ticket 63): it rides in `extra`, shown as the search
  // bar's chip.
  minYield: string;
  // Photo-intel renovation tiers (subset of PHOTO_TIERS values).
  photoConditions: string[];
  // Search-bar filters carried in from /listings: amenity tag ids and
  // the text search (services/search_text). Shown as chips, removable.
  featureTags: string[];
  q: string;
  // The rest of what the search bar can apply (owner, 2026-09-21:
  // "whatever we can apply in the magic search should also be
  // alertable"): who sells, the floor range, rent instead of sale.
  sellerType: "any" | "owner" | "agent" | "company";
  floorMin: string;
  floorMax: string;
  /** sale (the listings page's default) | rent | all (both). */
  leased: "sale" | "rent" | "all";
  geoMode: GeoMode;
  // Canonical bina-parity location IDs + complex slugs (same vocabulary as
  // the listings browse + the matcher), not display names.
  locationIds: string[];
  complexSlugs: string[];
  centerLat: number | null;
  centerLng: number | null;
  radiusKm: number;
  /** Drawn map areas ([lng, lat] rings) for geo mode "draw". */
  polygon: [number, number][][];
  hasRepair: Tristate;
  hasMortgage: Tristate;
  /** Sold with its loan (the listings panel's take-over switch) and its
   *  own controls; they only apply while the switch is on. */
  mortgageKind: "" | "takeover";
  maxInitial: string;
  mortgageRateMin: string;
  mortgageRateMax: string;
  mortgageTermsKnown: Tristate;
  mortgageYears: string[];
  hasKupcha: Tristate;
  excludeFirstFloor: boolean;
  excludeLastFloor: boolean;
  minVerdictTier: Tier;
  notifyFrequency: NotifyFrequency;
  /** Optional, as typed; empty makes the label from the filters. */
  name: string;
}

const ROOM_OPTIONS = [1, 2, 3, 4, 5];

/** The alert's kinds: a flat's build and a house (any mix of the three),
 *  then land, a commercial space and a garage, each on its own (the
 *  listings page's kinds, 2026-10-21). */
const ALERT_KINDS = ["new", "old", "house", "land", "commercial", "garage"] as const;
type AlertKind = (typeof ALERT_KINDS)[number];
const ALERT_KIND_LABEL = {
  new: "listings.filter.building_type.new",
  old: "listings.filter.building_type.old",
  house: "listings.filter.category.house",
  land: "listings.filter.category.land",
  commercial: "listings.filter.category.commercial",
  garage: "listings.filter.category.garage",
} as const;

// Photo-intel renovation tiers - same values + palette as the listings
// filter panel (and the residence page's renovation donut) so the tier
// colours mean the same thing everywhere.
const PHOTO_TIERS = [
  { value: "luxury", color: "#a78bfa" },
  { value: "modern", color: "#34d399" },
  { value: "mid",    color: "#38bdf8" },
  { value: "dated",  color: "#fbbf24" },
  { value: "shell",  color: "#fb7185" },
];

// Push channel UI (browser web push + native app push). Web push went
// live together with the mobile app release: VAPID keys are provisioned
// in prod, and inside the mobile shell the same card drives FCM app
// notifications instead of a browser subscription.
const PUSH_UI_ENABLED = true;

// Notification cadence options offered in the form (owner call
// 2026-07-18: instant / hourly / daily / every 3 days / weekly).
// Legacy "30m"/"6h" alerts keep working; editing one shows its value
// appended so it isn't silently coerced.
const FREQUENCY_OPTIONS: { value: NotifyFrequency; icon: typeof Zap }[] = OFFERED_FREQUENCIES.map(
  (value) => ({ value, icon: value === "instant" ? Zap : Clock }),
);

// Cadences a free alert may keep (lib/alert-options, the backend's
// _FREE_FREQUENCIES; the Telegram Mini App's sheet reads the same set).
const FREE_FREQUENCIES = FREE_ALERT_FREQUENCIES as ReadonlySet<NotifyFrequency>;

interface Estimate {
  total_active: number;
  weekly_estimate: number;
  max_weekly: number;
  too_broad: boolean;
  reasons: string[];
}

function NewAlertForm() {
  const { user, loading: authLoading } = useAuth();
  // Device-bound save landed (no account): the success view below
  // replaces the form.
  const [anonSaved, setAnonSaved] = useState<{ name: string } | null>(null);
  const isPaid = hasProFeatures(user);
  const { locale } = useLocale();
  const t = useT();
  const router = useRouter();
  const searchParams = useSearchParams();
  // ?edit={id} switches this form into edit mode: load the existing
  // saved search, prefill, and PATCH instead of POST on save.
  const editId = searchParams.get("edit");

  const [form, setForm] = useState<FormState>({
    roomCounts: [],
    listingKind: [],
    priceMin: "",
    priceMax: "",
    areaMin: "",
    areaMax: "",
    minYield: "",
    photoConditions: [],
    featureTags: [],
    q: "",
    sellerType: "any",
    floorMin: "",
    floorMax: "",
    leased: "sale",
    geoMode: "area",
    locationIds: [],
    complexSlugs: [],
    centerLat: null,
    centerLng: null,
    radiusKm: 2,
    polygon: [],
    hasRepair: "any",
    hasMortgage: "any",
    mortgageKind: "",
    maxInitial: "",
    mortgageRateMin: "",
    mortgageRateMax: "",
    mortgageTermsKnown: "any",
    mortgageYears: [],
    hasKupcha: "any",
    excludeFirstFloor: false,
    excludeLastFloor: false,
    minVerdictTier: "great_deal",
    notifyFrequency: "instant",
    name: "",
  });

  // Browse filters the form has no control for (deal tier, rent period,
  // minutes to the metro, excluded tags, ...). Kept verbatim and merged
  // into the saved blob, so an alert matches exactly the listings page it
  // was created from and an edit never drops them (owner, 2026-10-01).
  const [extra, setExtra] = useState<Record<string, unknown>>({});
  const [moreOpen, setMoreOpen] = useState(false);
  // An alert for a category the model does not value (land, commercial
  // premises, garages): no deal threshold, every new listing that fits.
  const unvalued = ["land", "commercial", "garage"].includes(String(extra.category ?? ""))
    || form.listingKind.includes("land");
  const separateAlert = extra.category === "commercial" || extra.category === "garage";
  const landAlert = form.listingKind.includes("land") || extra.category === "land";
  const alertCategory = separateAlert ? String(extra.category) : landAlert ? "land" : "";
  const propertyKinds = Array.isArray(extra.property_kinds) ? (extra.property_kinds as unknown[]).map(String) : [];

  // The kind (owner, 2026-10-21: "the alerts for garages, for offices,
  // and for commercial, all of them should be added"): a flat's build and
  // a house mix as before; land, a commercial space and a garage each
  // stand alone, the way the listings page's kinds do. Picking one drops
  // what no longer applies (the listings bar's kindChangePatch, in the
  // form's own fields).
  function pickKind(k: AlertKind) {
    const dropExtra = (keys: string[]) => setExtra((e) => {
      const n = { ...e };
      for (const key of keys) delete n[key];
      return n;
    });
    // Filters only a flat or a house has (a shop, a garage and a plot
    // have no rooms, residence, build, yield, mortgage or photo tier).
    const homeOnly: Partial<FormState> = {
      roomCounts: [], complexSlugs: [], minYield: "",
      photoConditions: [], featureTags: [], hasMortgage: "any", mortgageKind: "", maxInitial: "",
      mortgageRateMin: "", mortgageRateMax: "", mortgageTermsKnown: "any", mortgageYears: [],
      excludeFirstFloor: false, excludeLastFloor: false,
    };
    const homeExtras = [
      "deal_tiers", "rent_period", "developers", "building_type", "deed_gap", "exclude_tags",
      "total_floors_min", "total_floors_max",
    ];
    if (k === "commercial" || k === "garage") {
      const on = extra.category === k;
      setForm((f) => ({
        ...f, listingKind: [],
        ...(on ? {} : { ...homeOnly, ...(k === "garage" ? { floorMin: "", floorMax: "", hasRepair: "any" as Tristate } : {}) }),
      }));
      setExtra((e) => {
        const n = { ...e };
        for (const key of [...homeExtras, "category", "property_kinds", "ppm_min", "ppm_max", "sot_min", "sot_max", "pps_min", "pps_max"]) delete n[key];
        if (!on) n.category = k;
        return n;
      });
      return;
    }
    if (k === "land") {
      const on = landAlert;
      setForm((f) => ({
        ...f, listingKind: on ? [] : ["land"],
        ...(on ? {} : { ...homeOnly, floorMin: "", floorMax: "", areaMin: "", areaMax: "", hasRepair: "any" as Tristate }),
      }));
      dropExtra([...homeExtras, "category", "property_kinds", "ppm_min", "ppm_max", ...(on ? ["sot_min", "sot_max", "pps_min", "pps_max"] : [])]);
      return;
    }
    // A flat's build or a house: they mix with each other only.
    if (alertCategory) {
      setForm((f) => ({ ...f, listingKind: [k] }));
      dropExtra(["category", "property_kinds", "ppm_min", "ppm_max", "sot_min", "sot_max", "pps_min", "pps_max"]);
      return;
    }
    setForm((f) => ({
      ...f,
      listingKind: f.listingKind.includes(k) ? f.listingKind.filter((x) => x !== k) : [...f.listingKind, k],
    }));
    // A house next to a browse's "flats only" would match nothing.
    if (k === "house" && extra.category === "apartment") dropExtra(["category"]);
  }
  const kindOn = (k: AlertKind) => (k === "commercial" || k === "garage" ? extra.category === k : k === "land" ? landAlert : !alertCategory && form.listingKind.includes(k));
  // Kept keys the form now shows with its own controls (the kind, its
  // sub-kinds, a plot's sot), so they are not chips as well.
  const shownByForm = (k: string) => BAR_SHOWN.has(k) || k === "property_kinds"
    || (k === "category" && ["land", "commercial", "garage"].includes(String(extra.category)))
    || (landAlert && (k === "sot_min" || k === "sot_max"));
  function toggleProperty(k: string) {
    setExtra((e) => {
      const cur = Array.isArray(e.property_kinds) ? (e.property_kinds as unknown[]).map(String) : [];
      const next = cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k];
      const n = { ...e };
      if (next.length) n.property_kinds = next; else delete n.property_kinds;
      return n;
    });
  }
  // A plot's size in sot (the listings bar's sot chip), kept with the
  // other listings-only keys.
  const setSot = (key: "sot_min" | "sot_max", v: number | "") => setExtra((e) => {
    const n = { ...e };
    if (v === "") delete n[key]; else n[key] = v;
    return n;
  });

  const [telegramConnected, setTelegramConnected] = useState(false);
  // Pre-minted t.me deep link so the connect control can be a real <a>.
  // Mobile Safari blocks `window.open` called after `await fetch` because
  // the user-gesture token has expired by then; a plain anchor sidesteps
  // the popup blocker entirely.
  const [telegramLink, setTelegramLink] = useState<string | null>(null);
  // Web push - the second delivery channel, for eligible (non-iOS-tab)
  // devices. State lives here (not inside the card) so the submit gate
  // can accept push as an ALTERNATIVE to Telegram. Computed client-side
  // after mount to avoid an SSR / hydration mismatch.
  const [pushEligible, setPushEligible] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  // Running inside the native mobile shell (client-only; set on mount).
  const [inApp, setInApp] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushErr, setPushErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);
  // When the backend rejects a create as a duplicate (409), we surface a
  // friendly message that links to the existing alert.
  const [duplicateId, setDuplicateId] = useState<number | null>(null);
  // Progressive disclosure: the primary filters (rooms / location /
  // price) are always visible; the rest live behind this collapsed
  // "More filters" section. Auto-open it in edit mode when one of the
  // advanced filters is already set so the user sees their values.
  // Live "how busy will this alert be" estimate, refreshed (debounced)
  // whenever the filters change. Drives the too-broad warning + gates
  // submit so a firehose alert can't be created.
  const [estimate, setEstimate] = useState<Estimate | null>(null);
  const [estimating, setEstimating] = useState(false);

  // Edit mode: load the existing saved search and prefill the form.
  useEffect(() => {
    if (!user || !editId) return;
    let cancelled = false;
    void fetch(`${API_BASE}/alerts/${editId}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled || !d) return;
        const f = d.filters || {};
        setExtra(pickExtra(f));
        setForm((prev) => ({
          ...prev,
          featureTags: Array.isArray(f.feature_tags) ? f.feature_tags.map(String) : [],
          q: typeof f.q === "string" ? f.q : "",
          roomCounts: Array.isArray(f.room_counts) ? f.room_counts : [],
          listingKind: Array.isArray(f.listing_kind) ? f.listing_kind : [],
          priceMin: f.price_min ?? "",
          priceMax: f.price_max ?? "",
          areaMin: f.area_min ?? "",
          areaMax: f.area_max ?? "",
          minYield: f.min_yield != null ? String(f.min_yield) : "",
          photoConditions: Array.isArray(f.photo_conditions)
            ? f.photo_conditions.map(String)
            : [],
          sellerType: (["owner", "agent", "company"] as const).includes(f.seller_type) ? f.seller_type : "any",
          floorMin: f.floor_min != null ? String(f.floor_min) : "",
          floorMax: f.floor_max != null ? String(f.floor_max) : "",
          // Alerts saved before the control existed carry no transaction
          // and have always matched both; editing keeps that.
          leased: f.leased === true ? "rent" : f.leased === false ? "sale" : "all",
          geoMode: geoModeOf(f),
          // Canonical IDs/slugs. Legacy name-based alerts (location_names /
          // complex_names, never matched reliably) load with no location
          // selected; the user re-picks on the ID-based picker.
          locationIds: Array.isArray(f.location_ids) ? f.location_ids.map(String) : [],
          complexSlugs: Array.isArray(f.complex_slugs) ? f.complex_slugs.map(String) : [],
          centerLat: f.center_lat ?? null,
          centerLng: f.center_lng ?? null,
          radiusKm: f.radius_km ?? 2,
          polygon: Array.isArray(f.polygon) ? polygonParamsToRings(f.polygon.map(String)) : [],
          hasRepair: f.has_repair === "yes" || f.has_repair === "no" ? f.has_repair : "any",
          hasMortgage: f.has_mortgage === "yes" || f.has_mortgage === "no" ? f.has_mortgage : "any",
          ...mortgageFromBlob(f, prev),
          hasKupcha: f.has_kupcha === "yes" || f.has_kupcha === "no" ? f.has_kupcha : "any",
          excludeFirstFloor: f.exclude_first_floor === true,
          excludeLastFloor: f.exclude_last_floor === true,
          minVerdictTier: d.min_verdict_tier || "great_deal",
          // A typed name comes back into the box; an auto label stays
          // out and is rebuilt from the filters on save.
          name: d.custom_name && typeof d.name === "string" ? d.name : "",
          notifyFrequency: (
            (NOTIFY_FREQUENCIES as readonly string[]).includes(d.notify_frequency)
              ? d.notify_frequency
              : "instant"
          ) as NotifyFrequency,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user, editId]);

  // Free alerts ride the daily digest only - coerce whatever the
  // default / edit-load / prefill put in the form. Paid users keep
  // their pick. Anonymous builders count as free (they'd sign up as
  // free); waiting for authLoading avoids clamping a paid user's
  // prefill during the auth-resolution flash.
  useEffect(() => {
    if (!authLoading && !isPaid && !FREE_FREQUENCIES.has(form.notifyFrequency)) {
      setForm((f) => ({ ...f, notifyFrequency: "24h" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isPaid, form.notifyFrequency]);

  // Prefill from the browse filters (Create-alert button on /listings or
  // /map carries `?prefill=<base64 blob>`). Edit mode wins if both are
  // present. Mirrors the edit-mode population, but seeds from the decoded
  // blob and only touches the filter fields (the label is derived from
  // them automatically). Runs once for a given prefill payload.
  // Amenity vocabulary for the tag chips (labels per locale).
  const [tagVocab, setTagVocab] = useState<FeatureTagVocab | null>(null);
  useEffect(() => {
    let alive = true;
    getFeatureTags().then((v) => { if (alive) setTagVocab(v); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const prefillRaw = searchParams.get("prefill");
  useEffect(() => {
    if (editId) return;
    const f = decodePrefill(prefillRaw) as Record<string, any> | null;
    if (!f) return;
    setExtra(pickExtra(f));
    setForm((prev) => blobToForm(prev, f, false));
  }, [prefillRaw, editId]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    function refreshStatus() {
      void fetch(`${API_BASE}/alerts/telegram/status`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!cancelled) setTelegramConnected(Boolean(d?.linked)); })
        .catch(() => {});
    }
    refreshStatus();
    // Re-check on focus: after the user taps the Telegram deep link,
    // the bot binds the account in the background and the user comes
    // back to this tab. Polling on focus flips the button to
    // "Connected" without a manual refresh.
    window.addEventListener("focus", refreshStatus);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refreshStatus);
    };
  }, [user]);

  // Telegram is the only channel now, so mint a fresh link code whenever
  // the user isn't connected yet. Backend TTL is 15 min; we self-refresh
  // ~60s before expiry so the rendered anchor never points at a dead code.
  useEffect(() => {
    if (!user || telegramConnected) return;
    let cancelled = false;
    let refresh: ReturnType<typeof setTimeout> | undefined;

    async function mint() {
      try {
        const r = await fetch(`${API_BASE}/alerts/telegram/link`, {
          method: "POST", credentials: "include",
        });
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (cancelled || !data?.deep_link) return;
        setTelegramLink(data.deep_link);
        const ttl = data.expires_at
          ? Date.parse(data.expires_at) - Date.now() - 60_000
          : 0;
        if (ttl > 0) refresh = setTimeout(mint, ttl);
      } catch {}
    }

    void mint();
    return () => {
      cancelled = true;
      if (refresh) clearTimeout(refresh);
    };
  }, [user, telegramConnected]);

  // Resolve push eligibility + current subscription once on mount (both
  // are client-only). Inside the mobile shell the channel is FCM app
  // push: always eligible, and "subscribed" is the server's word on
  // whether this account has an active device token. In a browser it's
  // web push; an ineligible one (iOS Safari tab, or no Push API) keeps
  // pushEligible=false, so the option never shows.
  useEffect(() => {
    if (isNativeApp()) {
      setInApp(true);
      setPushEligible(true);
      void isAppPushRegistered().then(setPushSubscribed).catch(() => {});
      return;
    }
    setPushEligible(isPushEligible());
    void isThisDeviceSubscribed().then(setPushSubscribed).catch(() => {});
  }, []);

  async function togglePush() {
    setPushBusy(true);
    setPushErr(null);
    try {
      if (pushSubscribed) {
        if (inApp) {
          await unregisterAppPush();
        } else {
          await unsubscribeFromPush();
        }
        setPushSubscribed(false);
      } else {
        const r = inApp ? await registerAppPush() : await subscribeToPush();
        if (r.ok) {
          setPushSubscribed(true);
        } else {
          const key =
            r.reason === "denied"
              ? (inApp ? "alerts.form.push.app.err.denied" : "alerts.form.push.err.denied")
              : r.reason === "disabled"
                ? (inApp ? "alerts.form.push.app.err.disabled" : "alerts.form.push.err.disabled")
                : "alerts.form.push.err.generic";
          setPushErr(t(key as Parameters<typeof t>[0]));
        }
      }
    } finally {
      setPushBusy(false);
    }
  }

  function toggleRoom(n: number) {
    setForm((f) => ({
      ...f,
      roomCounts: f.roomCounts.includes(n)
        ? f.roomCounts.filter((r) => r !== n)
        : [...f.roomCounts, n].sort(),
    }));
  }

  // Build the filters blob the API + estimate both consume. Extracted so
  // the live estimate and the submit send EXACTLY the same shape.
  const filterBlob = useMemo(() => {
    const filters: Record<string, unknown> = { ...extra };
    if (form.roomCounts.length) filters.room_counts = form.roomCounts;
    if (form.listingKind.length) filters.listing_kind = form.listingKind;
    if (form.priceMin !== "") filters.price_min = form.priceMin;
    if (form.priceMax !== "") filters.price_max = form.priceMax;
    if (form.areaMin !== "") filters.area_min = form.areaMin;
    if (form.areaMax !== "") filters.area_max = form.areaMax;
    const minYield = Number(form.minYield);
    if (form.minYield !== "" && !Number.isNaN(minYield)) filters.min_yield = minYield;
    if (form.photoConditions.length) filters.photo_conditions = form.photoConditions;
    if (form.featureTags.length) filters.feature_tags = form.featureTags;
    if (form.q.trim()) filters.q = form.q.trim();
    if (form.sellerType !== "any") filters.seller_type = form.sellerType;
    const flMin = Number(form.floorMin);
    if (form.floorMin !== "" && !Number.isNaN(flMin)) filters.floor_min = flMin;
    const flMax = Number(form.floorMax);
    if (form.floorMax !== "" && !Number.isNaN(flMax)) filters.floor_max = flMax;
    if (form.leased === "rent") filters.leased = true;
    else if (form.leased === "sale") filters.leased = false;
    // Both sides, said out loud: without the key the API saves a sale.
    else filters.leased = null;
    if (form.hasRepair !== "any") filters.has_repair = form.hasRepair;
    if (form.hasMortgage !== "any") filters.has_mortgage = form.hasMortgage;
    // The take-over controls ride only while the switch is on, as on the
    // listings panel.
    if (form.mortgageKind === "takeover") {
      filters.mortgage_kind = "takeover";
      const mi = Number(form.maxInitial);
      if (form.maxInitial !== "" && !Number.isNaN(mi)) filters.max_initial = mi;
      const rmin = Number(form.mortgageRateMin);
      if (form.mortgageRateMin !== "" && !Number.isNaN(rmin)) filters.mortgage_rate_min = rmin;
      const rmax = Number(form.mortgageRateMax);
      if (form.mortgageRateMax !== "" && !Number.isNaN(rmax)) filters.mortgage_rate_max = rmax;
      if (form.mortgageTermsKnown !== "any") filters.mortgage_terms_known = form.mortgageTermsKnown;
      if (form.mortgageYears.length) filters.mortgage_years = form.mortgageYears;
    }
    if (form.hasKupcha !== "any") filters.has_kupcha = form.hasKupcha;
    if (form.excludeFirstFloor) filters.exclude_first_floor = true;
    if (form.excludeLastFloor) filters.exclude_last_floor = true;
    filters.geo_mode = form.geoMode;
    if (form.geoMode === "area") {
      if (form.locationIds.length) filters.location_ids = form.locationIds;
      if (form.complexSlugs.length) filters.complex_slugs = form.complexSlugs;
    } else if (form.geoMode === "radius") {
      if (form.centerLat !== null) filters.center_lat = form.centerLat;
      if (form.centerLng !== null) filters.center_lng = form.centerLng;
      filters.radius_km = form.radiusKm;
    } else {
      // The rings as the API's flat values; the matcher, the estimate
      // and the coverage map read them (services/draw_area).
      const polygon = ringsToPolygonParams(form.polygon);
      if (polygon.length) filters.polygon = polygon;
    }
    return filters;
  }, [form, extra]);

  // The search bar reads the alert as listings URL params and writes back
  // through the same converter the "Create alert" button uses.
  const magicSearchOn = useFeatureFlag("magic_search");
  const browseNow = useMemo(() => blobToBrowseParams(filterBlob), [filterBlob]);
  function applyBrowse(next: URLSearchParams) {
    const blob = browseParamsToAlertFilters(next as unknown as ReadonlyURLSearchParams);
    setExtra(pickExtra(blob));
    setForm((prev) => blobToForm(prev, blob, true));
  }

  // The listings filter sheet, so an alert offers every filter the
  // listings page does (owner, 2026-10-17: "source is not here"). It
  // hands back the keys it manages; the rest of the alert (text search,
  // drawn area, alert-only fields) stays as it was.
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelLoaded, setPanelLoaded] = useState(false);
  function applyPanel(params: Record<string, string | string[]>) {
    const next = new URLSearchParams(browseNow.toString());
    for (const k of PANEL_PARAM_KEYS) next.delete(k);
    for (const [k, v] of Object.entries(params)) {
      // The drawn area is the alert's own (its geo tab): the sheet only
      // hands back the one it was given.
      if (PANEL_CARRIED_KEYS.includes(k)) continue;
      (Array.isArray(v) ? v : [v]).forEach((x) => { if (x) next.append(k, x); });
    }
    applyBrowse(next);
  }
  // Filters only the sheet can set (source, developer, plot size, ...),
  // a min/max pair counted once.
  // The kind, its sub-kinds and a plot's sot have their own controls now.
  const panelOnlyCount = new Set(Object.keys(extra).filter((k) => !shownByForm(k)).map((k) => k.replace(/_(min|max)$/, ""))).size;

  // How many of the folded "more filters" are set.
  const moreCount = [
    // A shop's, a garage's or a plot's size sits in the kind's own row.
    !alertCategory && (form.areaMin !== "" || form.areaMax !== ""),
    form.floorMin !== "" || form.floorMax !== "",
    form.excludeFirstFloor || form.excludeLastFloor,
    form.hasRepair !== "any",
    form.hasMortgage !== "any" || form.mortgageKind === "takeover",
    form.sellerType !== "any",
    form.photoConditions.length > 0,
    form.minYield !== "",
  ].filter(Boolean).length;
  const hasMore = moreCount > 0;
  useEffect(() => {
    if (hasMore) setMoreOpen(true);
  }, [hasMore]);

  // Live estimate: debounce filter changes, ask the backend how many new
  // listings/week this would notify, surface a too-broad warning.
  // NOTE: location_names / complex_names aren't wired into the backend
  // browse filter yet, so the estimate is a lower bound when only those
  // are set; the UI labels it "~".
  const filterKey = JSON.stringify(filterBlob);
  useEffect(() => {
    // Public endpoint - anonymous builders get the live estimate too
    // (it's the value that makes building before signing up worthwhile).
    let cancelled = false;
    setEstimating(true);
    const tid = setTimeout(() => {
      void fetch(`${API_BASE}/alerts/estimate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: filterKey,
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((d) => { if (!cancelled) setEstimate(d); })
        .catch(() => { if (!cancelled) setEstimate(null); })
        .finally(() => { if (!cancelled) setEstimating(false); });
    }, 450);
    return () => { cancelled = true; clearTimeout(tid); };
  }, [filterKey]);

  // Channels are no longer a SAVE gate: email is the zero-setup
  // fallback (the matcher emails the digest to users with neither
  // Telegram nor push), so a signed-in user can always save. Telegram
  // and push remain as faster, opt-in upgrades - the connect card
  // below sells them, it doesn't block.
  const pushChannelUsable = PUSH_UI_ENABLED && pushEligible;
  const hasChannel = telegramConnected || (pushChannelUsable && pushSubscribed);
  const tooBroad = estimate?.too_broad ?? false;
  // Radius mode without a picked centre would 400 on the backend
  // ("radius_geo_requires_center_and_radius") - gate it client-side.
  const radiusNeedsCenter =
    form.geoMode === "radius" && (form.centerLat === null || form.centerLng === null);
  // Draw mode with nothing drawn would 400 too ("draw_geo_requires_polygon").
  const drawNeedsArea = form.geoMode === "draw" && form.polygon.length === 0;
  const blocked = submitting || tooBroad || radiusNeedsCenter || drawNeedsArea;
  // CTA carries the payoff: when the filter is healthy, the save button
  // itself says what it buys ("Save · ≈37 listings/wk") - the estimate
  // is already computed live, so the number is real. Capped at 200/wk:
  // above that the number reads as a firehose, not a payoff.
  const weeklyForCta =
    estimate && !estimate.too_broad
    && estimate.weekly_estimate > 0 && estimate.weekly_estimate <= 200
      ? estimate.weekly_estimate
      : null;
  const saveLabel =
    (user ? t("alerts.form.save") : t("alerts.form.anon.save"))
    + (weeklyForCta != null
      ? ` · ${t("alerts.form.save.count").replace("{n}", String(weeklyForCta))}`
      : "");
  // The single "why you can't create it yet" line, shown next to Save
  // (and in the mobile action bar). null = ready to create.
  const blockReason = tooBroad
    ? t("alerts.form.estimate.too_broad")
    : radiusNeedsCenter
      ? t("alerts.form.geo.center.gate")
    : drawNeedsArea
      ? t("alerts.form.geo.draw.gate")
      : null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitErr(null);
    setDuplicateId(null);
    // Range sanity: min <= max for price and area. Caught here so the
    // user gets a localized message instead of the backend's raw
    // "price_range_inverted" / "area_range_inverted" slugs.
    if (form.priceMin !== "" && form.priceMax !== "" && form.priceMin > form.priceMax) {
      setSubmitErr(t("alerts.form.err.price_range"));
      return;
    }
    if (form.areaMin !== "" && form.areaMax !== "" && form.areaMin > form.areaMax) {
      setSubmitErr(t("alerts.form.err.area_range"));
      return;
    }
    // Anonymous: the alert is built - now (and only now) ask for the
    // account. The whole draft (filters + tier + frequency) rides the
    // ?prefill= blob through login via return_to, so the form comes
    // back populated and the user just connects a channel and saves.
    if (!user) {
      // Device path first (2026-09-20): one saved search per browser,
      // delivered by push to that browser, no account. The browser's
      // push subscription is the identity (same as the residence
      // follow). Refused push, the one-per-device ladder, or any other
      // failure falls through to the account path below.
      setSubmitting(true);
      try {
        const { sub } = await getOrCreatePushSubscription("/follows/config");
        if (sub) {
          const r = await fetch(`${API_BASE}/alerts/anon`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              filters: filterBlob,
              min_verdict_tier: unvalued ? "any" : form.minVerdictTier,
              subscription: sub.toJSON(),
            }),
          });
          if (r.ok) {
            const d = await r.json().catch(() => ({}));
            haptic([10, 50, 10]);
            setAnonSaved({ name: typeof d.name === "string" ? d.name : "" });
            return;
          }
          const detail = (await r.json().catch(() => ({})))?.detail;
          if (detail && typeof detail === "object" && detail.reason === "too_broad") {
            setSubmitErr(t("alerts.form.estimate.too_broad"));
            return;
          }
        }
      } catch {
        /* fall through to the account path */
      } finally {
        setSubmitting(false);
      }
      const draft = {
        ...filterBlob,
        min_verdict_tier: unvalued ? "any" : form.minVerdictTier,
        notify_frequency: form.notifyFrequency,
      };
      const returnTo = `/alerts/new?prefill=${encodePrefill(draft)}`;
      router.push(`/${locale}/auth/login?return_to=${encodeURIComponent(returnTo)}`);
      return;
    }
    setSubmitting(true);
    const filters = filterBlob;

    try {
      const r = await fetch(
        editId ? `${API_BASE}/alerts/${editId}` : `${API_BASE}/alerts`,
        {
          method: editId ? "PATCH" : "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // A typed name is kept; empty, the backend makes the label
            // from the filters.
            name: form.name.trim() || null,
            filters,
            min_verdict_tier: unvalued ? "any" : form.minVerdictTier,
            channel: "telegram",
            notify_frequency: form.notifyFrequency,
          }),
        },
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        // too_broad comes back as a structured detail object; show the
        // friendly localized warning instead of the raw payload.
        const detail = e.detail;
        if (detail && typeof detail === "object" && detail.reason === "duplicate") {
          // 409: an identical alert already exists - point the user at it.
          setDuplicateId(typeof detail.id === "number" ? detail.id : null);
          setSubmitErr(t("alerts.form.duplicate"));
        } else if (detail && typeof detail === "object" && detail.reason === "too_broad") {
          setSubmitErr(t("alerts.form.estimate.too_broad"));
        } else if (detail && typeof detail === "object" && detail.reason === "limit_reached") {
          setSubmitErr(
            detail.tier === "paid"
              ? t("alerts.limit.paid")
              : t("alerts.limit.free"),
          );
        } else if (detail && typeof detail === "object" && detail.reason === "frequency_paid") {
          setSubmitErr(t("alerts.frequency.free_hint"));
        } else if (detail === "radius_geo_requires_center_and_radius") {
          setSubmitErr(t("alerts.form.geo.center.gate"));
        } else if (detail === "draw_geo_requires_polygon") {
          setSubmitErr(t("alerts.form.geo.draw.gate"));
        } else if (detail === "price_range_inverted") {
          setSubmitErr(t("alerts.form.err.price_range"));
        } else if (detail === "area_range_inverted") {
          setSubmitErr(t("alerts.form.err.area_range"));
        } else {
          setSubmitErr(typeof detail === "string" ? detail : `Error ${r.status}`);
        }
        return;
      }
      // Fresh alert (not an edit): the /alerts list greets it with a
      // one-shot praise toast + haptic tick (?created=1, stripped there).
      trackEvent("alert.save", {
        edit: !!editId, named: !!form.name.trim(),
        street: Array.isArray(extra.street) && extra.street.length > 0,
        frequency: form.notifyFrequency, leased: form.leased,
      });
      if (!editId) haptic([10, 50, 10]);
      router.push(editId ? "/alerts" : "/alerts?created=1");
    } catch (e: unknown) {
      setSubmitErr(e instanceof Error ? e.message : "Network error");
    } finally {
      setSubmitting(false);
    }
  }

  // Auth gate - placed AFTER all hooks (Rules of Hooks): the sign-in prompt
  // must not change the number of hooks called between renders, or React
  // throws "rendered more hooks than during the previous render" when auth
  // resolves from loading -> loaded.
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <SharedHeader />
        <main className="max-w-2xl mx-auto px-4 py-16 flex justify-center">
          <AnimatedLogoMark size={36} active loop />
        </main>
      </div>
    );
  }
  if (anonSaved) {
    const draft = {
      ...filterBlob,
      min_verdict_tier: unvalued ? "any" : form.minVerdictTier,
      notify_frequency: form.notifyFrequency,
    };
    const registerTo = `/auth/register?return_to=${encodeURIComponent(`/alerts/new?prefill=${encodePrefill(draft)}`)}`;
    return (
      <div className="min-h-screen bg-gray-50">
        <SharedHeader />
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
                <BellRing className="h-4.5 w-4.5" />
              </span>
              <div className="min-w-0">
                <h1 className="text-xl font-semibold tracking-tight text-gray-900">
                  {t("alerts.form.anon.device.title")}
                </h1>
                {anonSaved.name && (
                  <p className="mt-1 text-sm font-medium text-gray-700">{anonSaved.name}</p>
                )}
                <p className="mt-2 text-sm text-gray-600">{t("alerts.form.anon.device.body")}</p>
              </div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-2.5">
              <Link
                href={registerTo}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
              >
                {t("alerts.form.anon.device.cta")}
              </Link>
              <Link
                href="/listings"
                className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {t("alerts.form.anon.device.browse")}
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Anonymous visitors fall through to the full builder - the account
  // ask happens at save time (see handleSubmit), not at the door.

  // Signed in but email not confirmed: block alert creation with the
  // verify prompt (POST /api/alerts also returns 403 email_not_verified).
  if (user && user.email_verified === false) {
    return <VerifyEmailGate />;
  }

  // The folded filters a shop shares with a home, drawn once.
  const groundOnly = form.floorMin === "1" && form.floorMax === "1";
  const floorRangeSection = (
    <Section label={t("alerts.form.floor_range")}>
      <div className="grid grid-cols-2 gap-2">
        <RawNumberInput
          value={form.floorMin}
          onChange={(v) => setForm((f) => ({ ...f, floorMin: v }))}
          placeholder="2"
        />
        <RawNumberInput
          value={form.floorMax}
          onChange={(v) => setForm((f) => ({ ...f, floorMax: v }))}
          placeholder="9"
        />
      </div>
    </Section>
  );
  const repairSection = (
    <Section label={t("alerts.form.repair")}>
      <Tristate
        value={form.hasRepair}
        onChange={(v) => setForm((f) => ({ ...f, hasRepair: v }))}
        labels={{
          any: t("alerts.form.bool.any"),
          yes: t("alerts.form.bool.yes"),
          no: t("alerts.form.bool.no"),
        }}
      />
    </Section>
  );
  const sellerTypeSection = (
    <Section label={t("listings.filter.seller_type")}>
      <div className="flex gap-1.5">
        {(["any", "owner", "agent", "company"] as const).map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => setForm((f) => ({ ...f, sellerType: opt }))}
            aria-pressed={form.sellerType === opt}
            className={`${choiceBtn(form.sellerType === opt)} flex-auto px-2`}
          >
            {t(`listings.filter.seller_type.${opt}`)}
          </button>
        ))}
      </div>
    </Section>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />
      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {/* Mobile keeps ONLY the title - the back link, icon and
            subtitle each cost a row of a viewport the form itself
            needs (nav + OS back already cover the way out). */}
        <Link
          href="/alerts"
          className="hidden sm:inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 dark:hover:text-white mb-3"
        >
          <ChevronLeft className="w-4 h-4" />
          {t("nav.alerts")}
        </Link>

        {/* A plain <div>, NOT <header>: globals.css has a `.dark header`
            background+border rule (for the site nav) that would otherwise
            paint an awkward dark band behind this section title. */}
        <div className="mb-4 sm:mb-5">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
            {editId ? t("alerts.form.title.edit") : t("alerts.form.title.create")}
          </h1>
          <p className="mt-1 hidden sm:block text-sm text-gray-600 leading-relaxed">
            {t("alerts.subtitle")}
          </p>
        </div>

        <form
          id="new-alert-form"
          onSubmit={handleSubmit}
          className="space-y-4"
        >
          {/* Primary filters - rooms, location, price - grouped in one
              card so the form reads as a few clear blocks instead of a
              long stack of separate boxes (lower cognitive load). The
              auto-label preview moves down next to the Save button. */}
          <div className="rounded-2xl border border-gray-200 bg-white divide-y divide-gray-100 dark:divide-white/5">
            {/* The listings page's own search bar: the same parser and the
                same chips, so an alert is built exactly like a search and
                nothing typed there is lost here (owner, 2026-10-01). */}
            <div className="p-4">
              <SectionLabel>{t("alerts.form.search")}</SectionLabel>
              <MagicSearchBar
                aiEnabled={magicSearchOn}
                searchParams={browseNow}
                onClearAll={() => applyBrowse(new URLSearchParams())}
                onApplyAll={(params) => {
                  const next = new URLSearchParams();
                  for (const [k, v] of Object.entries(params)) {
                    if (Array.isArray(v)) v.forEach((x) => next.append(k, x));
                    else next.set(k, v);
                  }
                  applyBrowse(next);
                }}
                onMergeOne={(params) => {
                  const next = new URLSearchParams(browseNow.toString());
                  for (const [k, v] of Object.entries(params)) {
                    if (Array.isArray(v)) {
                      const prev = next.getAll(k);
                      next.delete(k);
                      [...prev.filter((x) => !v.includes(x)), ...v].forEach((x) => next.append(k, x));
                    } else next.set(k, v);
                  }
                  applyBrowse(next);
                }}
                onReplaceOne={(params) => {
                  const next = new URLSearchParams(browseNow.toString());
                  for (const [k, v] of Object.entries(params)) {
                    next.delete(k);
                    if (Array.isArray(v)) v.forEach((x) => next.append(k, x));
                    else if (v != null) next.set(k, v);
                  }
                  applyBrowse(next);
                }}
              />
              <button
                type="button"
                onClick={() => { setPanelLoaded(true); setPanelOpen(true); }}
                className="relative mt-2 inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 sm:w-auto"
              >
                <ListFilter className="h-5 w-5" />
                <span>{t("listings.filter.more")}</span>
                {panelOnlyCount > 0 && (
                  <span className={`absolute -right-1.5 -top-1.5 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px] font-bold ${FILTER_COUNT}`}>
                    {panelOnlyCount}
                  </span>
                )}
              </button>
            </div>
            {/* Order follows what production alerts actually carry (25
                alerts, 2026-09-20): rooms 21, price 19, kind 16, deed 12,
                location 11 - so kind and deed sit up here, not behind a
                fold, and location follows them. */}
            {/* Two short controls share a row on desktop (owner,
                2026-10-01: "some stuff takes a small fraction, but it's
                the only element on that row"); every choice is the same
                full-width button. */}
            <div className="grid gap-4 p-4 sm:grid-cols-2">
              <Section label={t("listings.filter.transaction")}>
                <div className="flex gap-1.5">
                  {(["sale", "rent", "all"] as const).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, leased: v }))}
                      aria-pressed={form.leased === v}
                      className={choiceBtn(form.leased === v)}
                    >
                      {t(v === "sale" ? "listings.filter.transaction.sale" : v === "rent" ? "listings.filter.transaction.rent" : "listings.filter.transaction.both")}
                    </button>
                  ))}
                </div>
              </Section>
              {/* The kind: a flat's build, a house, land, a commercial
                  space or a garage; under a commercial space its two kinds
                  (Ofis, Obyekt), under a garage a box or a parking place,
                  as on the listings page. */}
              <Section label={t("listings.filter.category")}>
                <div className="grid grid-cols-3 gap-1.5">
                  {ALERT_KINDS.map((k) => {
                    const on = kindOn(k);
                    return (
                      <button key={k} type="button" aria-pressed={on} onClick={() => pickKind(k)} className={choiceBtn(on)}>
                        {t(ALERT_KIND_LABEL[k])}
                      </button>
                    );
                  })}
                </div>
                {separateAlert && (
                  <>
                    <p className="mb-1.5 mt-3 text-xs font-semibold text-gray-500">{t("listings.filter.property_kind")}</p>
                    <div className="grid grid-cols-2 gap-1.5" role="group" aria-label={t("listings.filter.property_kind")}>
                      {kindsOf(alertCategory).map((k) => {
                        const on = propertyKinds.includes(k);
                        return (
                          <button key={k} type="button" aria-pressed={on} onClick={() => toggleProperty(k)} className={choiceBtn(on)}>
                            {t(kindChipKey(k))}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </Section>
            </div>
            <div className="grid gap-4 p-4 sm:grid-cols-2">
              {/* A shop's or a garage's size sits where a home's rooms
                  sit, a plot's in sot (the listings bar does the same). */}
              {separateAlert ? (
              <Section label={t("alerts.form.area_range")}>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput value={form.areaMin} onChange={(v) => setForm((f) => ({ ...f, areaMin: v }))} placeholder={alertCategory === "garage" ? "15" : "50"} />
                  <NumberInput value={form.areaMax} onChange={(v) => setForm((f) => ({ ...f, areaMax: v }))} placeholder={alertCategory === "garage" ? "40" : "300"} />
                </div>
              </Section>
              ) : landAlert ? (
              <Section label={t("listings.filter.sot")}>
                <div className="grid grid-cols-2 gap-2">
                  <NumberInput value={typeof extra.sot_min === "number" ? extra.sot_min : extra.sot_min ? Number(extra.sot_min) || "" : ""} onChange={(v) => setSot("sot_min", v)} placeholder="2" />
                  <NumberInput value={typeof extra.sot_max === "number" ? extra.sot_max : extra.sot_max ? Number(extra.sot_max) || "" : ""} onChange={(v) => setSot("sot_max", v)} placeholder="20" />
                </div>
              </Section>
              ) : (
              <Section label={t("alerts.form.rooms")}>
                <div className="grid grid-cols-5 gap-1.5">
                  {ROOM_OPTIONS.map((n) => {
                    const active = form.roomCounts.includes(n);
                    return (
                      <button key={n} type="button" aria-pressed={active} onClick={() => toggleRoom(n)} className={choiceBtn(active)}>
                        {n === 5 ? "5+" : n}
                      </button>
                    );
                  })}
                </div>
              </Section>
              )}
              <Section label={t("alerts.form.kupcha")}>
                <Tristate
                  value={form.hasKupcha}
                  onChange={(v) => setForm((f) => ({ ...f, hasKupcha: v }))}
                  labels={{
                    any: t("alerts.form.bool.any"),
                    yes: t("alerts.form.bool.yes"),
                    no: t("alerts.form.bool.no"),
                  }}
                />
              </Section>
            </div>

            {/* Price */}
            <div className="p-4">
              <SectionLabel>{t("alerts.form.price_range")}</SectionLabel>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput
                  value={form.priceMin}
                  onChange={(v) => setForm((f) => ({ ...f, priceMin: v }))}
                  placeholder="0"
                />
                <NumberInput
                  value={form.priceMax}
                  onChange={(v) => setForm((f) => ({ ...f, priceMax: v }))}
                  placeholder="500 000"
                />
              </div>
            </div>
            {Object.keys(extra).some((k) => !shownByForm(k)) && (
              <div className="p-4">
                <SectionLabel>{t("alerts.form.extra")}</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(extra).filter(([k]) => !shownByForm(k)).map(([k, v]) => (
                    <span
                      key={k}
                      className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-white py-1 pl-3 pr-1.5 text-xs font-medium text-gray-700"
                    >
                      {extraLabel(k, v, t, tagVocab, locale)}
                      <button
                        type="button"
                        onClick={() => setExtra((e) => { const n = { ...e }; delete n[k]; return n; })}
                        aria-label={t("alerts.form.extra.remove")}
                        className="rounded-full p-0.5 text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
            {/* Geo */}
            <div className="p-4">
              <SectionLabel>{t("alerts.form.geo")}</SectionLabel>
              <div className="mb-3 flex gap-1.5">
                {(["area", "radius", "draw"] as GeoMode[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    aria-pressed={form.geoMode === m}
                    onClick={() => setForm((f) => ({ ...f, geoMode: m }))}
                    className={`${choiceBtn(form.geoMode === m)} inline-flex items-center justify-center gap-1.5`}
                  >
                    {m === "area" ? <MapPin className="h-3.5 w-3.5" /> : m === "radius" ? <MapIcon className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                    {m === "area" ? t("alerts.form.geo.area_tab") : m === "radius" ? t("alerts.form.geo.radius_tab") : t("alerts.form.geo.draw_tab")}
                  </button>
                ))}
              </div>
              {form.geoMode === "area" ? (
                // Same district / metro / qəsəbə / nişangah / complex picker
                // the listings + map browse use, so an alert speaks the same
                // canonical location vocabulary as the matcher (and a browse
                // filter prefills exactly).
                <ListingsLocationPicker
                  selectedIds={form.locationIds}
                  onChange={(ids) => setForm((f) => ({ ...f, locationIds: ids }))}
                  // A residence is a place of homes: none for a shop or a
                  // garage (the listings bar does the same).
                  selectedComplexSlugs={separateAlert ? undefined : form.complexSlugs}
                  onComplexChange={separateAlert ? undefined : (slugs) => setForm((f) => ({ ...f, complexSlugs: slugs }))}
                  onApply={(ids, slugs) =>
                    setForm((f) => ({ ...f, locationIds: ids, complexSlugs: separateAlert ? [] : slugs }))
                  }
                />
              ) : form.geoMode === "radius" ? (
                <div className="space-y-3">
                  <LeafletRadiusPicker
                    center={
                      form.centerLat !== null && form.centerLng !== null
                        ? [form.centerLat, form.centerLng]
                        : null
                    }
                    radiusKm={form.radiusKm}
                    onChange={(lat, lng) =>
                      setForm((f) => ({ ...f, centerLat: lat, centerLng: lng }))
                    }
                  />
                  {form.centerLat === null && (
                    <p className="text-xs text-gray-500 text-center">
                      {t("alerts.form.geo.center")}
                    </p>
                  )}
                  <label className="block">
                    <span className="text-xs font-medium text-gray-500">
                      {t("alerts.form.geo.radius_km")}: {form.radiusKm} km
                    </span>
                    <input
                      type="range"
                      min={0.5}
                      max={10}
                      step={0.5}
                      value={form.radiusKm}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, radiusKm: Number(e.target.value) }))
                      }
                      className="w-full mt-2"
                      style={rangeFill(form.radiusKm, 0.5, 10)}
                    />
                  </label>
                </div>
              ) : (
                <AreaDrawPicker
                  rings={form.polygon}
                  onChange={(rings) => setForm((f) => ({ ...f, polygon: rings }))}
                />
              )}
            </div>

          </div>

          {/* The rest, always visible (owner: a folded group goes
              unnoticed), ordered by the same data: area 10, floor
              position 10, repair 9, mortgage 4, photo tier 2, yield 1.
              The number of floors (3) is set in the listings filter
              sheet since ticket 63. */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            {/* Folded by default (owner, 2026-10-01); opens by itself when
                a filter inside is already set, and the count says so. */}
            <button
              type="button"
              onClick={() => setMoreOpen((o) => !o)}
              aria-expanded={moreOpen}
              className="flex w-full items-center justify-between gap-3 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded-lg"
            >
              <span className="text-sm font-semibold text-gray-900 dark:text-white">
                {t("alerts.form.more_filters")}
                {moreCount > 0 && (
                  <span className={`ml-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold tabular-nums ${FILTER_COUNT}`}>{moreCount}</span>
                )}
              </span>
              <ChevronDown className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${moreOpen ? "rotate-180" : ""}`} />
            </button>
            {/* A shop: its floors, the ground floor in one tap (the
                listings sheet's pill), repair and the seller; a garage
                or a plot: the seller. Nothing of a flat's leaks in. */}
            {moreOpen && alertCategory !== "" && (
            <div className="mt-4 space-y-6">
              {alertCategory === "commercial" && (
                <>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
                    {floorRangeSection}
                    <Section label={t("listings.filter.floor_position")}>
                      <div className="flex gap-1.5">
                        <button
                          type="button"
                          aria-pressed={groundOnly}
                          onClick={() => setForm((f) => ({ ...f, floorMin: groundOnly ? "" : "1", floorMax: groundOnly ? "" : "1" }))}
                          className={choiceBtn(groundOnly)}
                        >
                          {t("listings.filter.ground_floor")}
                        </button>
                      </div>
                    </Section>
                  </div>
                  <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
                    {repairSection}
                    {sellerTypeSection}
                  </div>
                </>
              )}
              {alertCategory !== "commercial" && sellerTypeSection}
            </div>
            )}
            {moreOpen && alertCategory === "" && (
            <div className="mt-4 space-y-6">
          {/* Paired so each row is used end to end on desktop. */}
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
            <Section label={t("alerts.form.area_range")}>
              <div className="grid grid-cols-2 gap-2">
                <NumberInput
                  value={form.areaMin}
                  onChange={(v) => setForm((f) => ({ ...f, areaMin: v }))}
                  placeholder="40"
                />
                <NumberInput
                  value={form.areaMax}
                  onChange={(v) => setForm((f) => ({ ...f, areaMax: v }))}
                  placeholder="200"
                />
              </div>
            </Section>
            {floorRangeSection}
            </div>
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
            {repairSection}
            <Section label={t("alerts.form.mortgage")}>
              <Tristate
                value={form.hasMortgage}
                onChange={(v) => setForm((f) => ({ ...f, hasMortgage: v }))}
                labels={{
                  any: t("alerts.form.bool.any"),
                  yes: t("alerts.form.bool.yes"),
                  no: t("alerts.form.bool.no"),
                }}
              />
            </Section>
            </div>
            {/* Sold with its loan: the take-over switch and, once on, the
                listings panel's own controls (owner, 2026-10-16: "I don't
                think that I can select the mortgage" in the alert form). */}
            <Section label={t("listings.filter.mortgage_takeover")}>
              <button
                type="button"
                aria-pressed={form.mortgageKind === "takeover"}
                onClick={() => setForm((f) => (f.mortgageKind === "takeover"
                  ? { ...f, mortgageKind: "", maxInitial: "", mortgageRateMin: "", mortgageRateMax: "", mortgageTermsKnown: "any", mortgageYears: [] }
                  : { ...f, mortgageKind: "takeover" }))}
                className={`${choiceBtn(form.mortgageKind === "takeover")} w-full px-3 sm:w-auto sm:flex-none`}
              >
                {t("listings.filter.mortgage_takeover.btn")}
              </button>
              {form.mortgageKind === "takeover" && (
                <div className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4">
                  <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
                    <Section label={t("listings.filter.max_initial")}>
                      <RawNumberInput
                        value={form.maxInitial}
                        onChange={(v) => setForm((f) => ({ ...f, maxInitial: v }))}
                        placeholder="120000"
                      />
                    </Section>
                    <Section label={t("listings.filter.mortgage_terms")}>
                      <Tristate
                        value={form.mortgageTermsKnown}
                        onChange={(v) => setForm((f) => ({ ...f, mortgageTermsKnown: v }))}
                        labels={{
                          any: t("alerts.form.bool.any"),
                          yes: t("alerts.form.bool.yes"),
                          no: t("alerts.form.bool.no"),
                        }}
                      />
                    </Section>
                    <Section label={t("listings.filter.mortgage_rate")}>
                      <div className="grid grid-cols-2 gap-2">
                        {(["mortgageRateMin", "mortgageRateMax"] as const).map((key) => (
                          <div key={key} className="relative">
                            <RawNumberInput
                              value={form[key]}
                              onChange={(v) => setForm((f) => ({ ...f, [key]: v }))}
                              placeholder={key === "mortgageRateMin" ? "4" : "12"}
                              decimal
                              className="pr-9"
                            />
                            <span aria-hidden className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-base text-gray-400">%</span>
                          </div>
                        ))}
                      </div>
                    </Section>
                    <Section label={t("listings.filter.mortgage_years")}>
                      <div className="flex flex-wrap gap-1.5">
                        {(["le5", "6-10", "11-15", "gt15"] as const).map((b) => {
                          const on = form.mortgageYears.includes(b);
                          return (
                            <button
                              key={b}
                              type="button"
                              aria-pressed={on}
                              onClick={() => setForm((f) => ({ ...f, mortgageYears: on ? f.mortgageYears.filter((x) => x !== b) : [...f.mortgageYears, b] }))}
                              className={`h-9 rounded-lg border px-2.5 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
                                on ? SELECTED : UNSELECTED
                              }`}
                            >
                              {t(`listings.filter.mortgage_years.${b}` as Parameters<typeof t>[0])}
                            </button>
                          );
                        })}
                      </div>
                    </Section>
                  </div>
                </div>
              )}
            </Section>
            {/* The seller's four choices take the whole row (as for a
                garage or a plot); the floor position pairs with the yield
                now that the number of floors is the sheet's (ticket 63). */}
            {sellerTypeSection}
            <div className="grid grid-cols-1 gap-x-4 gap-y-5 sm:grid-cols-2">
            <Section label={t("listings.filter.floor")}>
              <div className="flex gap-1.5">
                {([
                  { key: "excludeFirstFloor", label: t("listings.filter.exclude_first_floor"), on: form.excludeFirstFloor },
                  { key: "excludeLastFloor", label: t("listings.filter.exclude_last_floor"), on: form.excludeLastFloor },
                ] as const).map(({ key, label, on }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, [key]: !on }))}
                    aria-pressed={on}
                    className={choiceBtn(on)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </Section>
            <Section label={t("alerts.form.min_yield")}>
              <div className="relative">
                <RawNumberInput
                  value={form.minYield}
                  onChange={(v) => setForm((f) => ({ ...f, minYield: v }))}
                  placeholder="8"
                  decimal
                  className="pr-9"
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-y-0 right-3.5 flex items-center text-base text-gray-400"
                >
                  %
                </span>
              </div>
            </Section>
            </div>
            <Section label={t("listings.filter.photo_tier")}>
              <div className="flex flex-wrap gap-2">
                {PHOTO_TIERS.map(({ value, color }) => {
                  const on = form.photoConditions.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() =>
                        setForm((f) => ({
                          ...f,
                          photoConditions: on
                            ? f.photoConditions.filter((x) => x !== value)
                            : [...f.photoConditions, value],
                        }))
                      }
                      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                        on ? SELECTED : UNSELECTED
                      }`}
                    >
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: color }}
                      />
                      {t(`photointel.tier.${value}` as Parameters<typeof t>[0])}
                    </button>
                  );
                })}
              </div>
            </Section>

            </div>
            )}
          </div>

          {/* Land, commercial premises and garages carry no valuation:
              no deal threshold to pick (the matcher ignores one). */}
          {!unvalued && (
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
          {/* Threshold */}
            <Section label={t("alerts.form.threshold")}>
              <p className="text-xs leading-relaxed text-gray-500 mb-3">
                {t("alerts.form.threshold.help")}
              </p>
              <div className="space-y-2">
                {(
                  ["any", "good_deal", "great_deal", "exceptional_deal"] as Tier[]
                ).map((tier) => {
                  const active = form.minVerdictTier === tier;
                  return (
                    <label
                      key={tier}
                      className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                        active
                          ? SELECTED
                          : "border-gray-200 bg-white hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="radio"
                        name="tier"
                        value={tier}
                        checked={active}
                        onChange={() =>
                          setForm((f) => ({ ...f, minVerdictTier: tier }))
                        }
                        className="accent-indigo-600 mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                          {t(`alerts.tier.${tier}.title` as const)}
                        </span>
                        <span className="block text-xs text-gray-500 mt-0.5 leading-relaxed">
                          {t(`alerts.tier.${tier}.body` as const)}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] leading-relaxed text-gray-500">
                {t("alerts.form.threshold.footnote")}
              </p>
            </Section>
          </div>
          )}

          {/* Delivery cadence. Free alerts always ride the daily digest
              (coerced by an effect above), so free users see a single
              daily line + an upsell instead of three rows with two locked
              - fewer decisions, clearer upgrade path. Paid users pick. */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <SectionLabel>{t("alerts.form.frequency")}</SectionLabel>
            {/* Horizontal segmented control. Paid: all five selectable.
                Free: only the daily digest is selectable; the faster
                cadences show a PRO tag and link to pricing (free is
                always daily). */}
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {(FREQUENCY_OPTIONS.some((o) => o.value === form.notifyFrequency)
                ? FREQUENCY_OPTIONS
                // Editing a legacy 30m/6h alert: show its value as an
                // extra option instead of silently coercing it.
                : [...FREQUENCY_OPTIONS, { value: form.notifyFrequency, icon: Clock }]
              ).map(({ value, icon: Icon }) => {
                const active = form.notifyFrequency === value;
                const locked = !isPaid && !FREE_FREQUENCIES.has(value);
                const cls = `relative flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 text-center transition-colors ${
                  active
                    ? SELECTED
                    : locked
                      ? "border-gray-200 bg-gray-50 text-gray-400 dark:bg-white/5"
                      : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                }`;
                const inner = (
                  <>
                    <Icon className="w-4 h-4" />
                    <span className="text-xs font-semibold leading-tight">
                      {t(`alerts.frequency.${value}` as const)}
                    </span>
                    {locked && (
                      <span className={`absolute -top-1.5 -right-1.5 rounded-full px-1.5 py-0.5 text-xs font-bold ${PRO_BADGE}`}>
                        Pro
                      </span>
                    )}
                  </>
                );
                return locked ? (
                  <Link key={value} href="/pricing" className={cls}>
                    {inner}
                  </Link>
                ) : (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, notifyFrequency: value }))}
                    className={cls}
                  >
                    {inner}
                  </button>
                );
              })}
            </div>
            {!isPaid && (
              <p className="mt-2.5 text-[11px] leading-relaxed text-gray-400">
                {t("alerts.frequency.free_hint")}
              </p>
            )}
          </div>

          {/* Push channel. In a browser: web push for eligible
              (push-capable, non-iOS-tab) devices. In the mobile shell:
              native app notifications via FCM. The card hides itself
              when neither applies. Only rendered once a channel already
              exists - before that, the top-of-form channel card owns
              the push offer and two identical offers read as a bug. */}

          {/* Live volume estimate + too-broad gate. */}
          <EstimatePanel estimate={estimate} estimating={estimating} t={t} />

          {submitErr && (
            <div className="px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-xs text-rose-800 font-medium">
              <p>{submitErr}</p>
              {duplicateId !== null && (
                <Link
                  href={`/alerts/${duplicateId}`}
                  className="mt-1 inline-flex items-center gap-1 font-semibold text-rose-900 underline underline-offset-2 hover:text-rose-700"
                >
                  {t("alerts.form.duplicate.view")}
                </Link>
              )}
            </div>
          )}

          {/* Desktop: a "why you can't save yet" reason + the inline
              submit row. Mobile uses the fixed action bar below instead. */}
          {/* Delivery channel - at the END of the form as a plain card
              with one row per channel (owner, 2026-09-20: the indigo hero
              card at the top read as an AI feature and pushed the filters
              down). Saving stays gated on a connected channel; the gate
              message below the actions points here. */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <SectionLabel>{t("alerts.form.channel.title")}</SectionLabel>
            {!user ? (
              <p className="text-xs leading-relaxed text-gray-500">{t("alerts.form.anon.channel")}</p>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-white/5">
                {pushChannelUsable && (
                  <div className="flex items-center gap-3 py-2.5">
                    <Bell className="h-4 w-4 shrink-0 text-gray-400" />
                    <span className="min-w-0 flex-1 text-sm text-gray-800">
                      {inApp ? t("alerts.form.push.app") : t("alerts.form.push")}
                    </span>
                    {pushSubscribed ? (
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                        <Check className="h-3.5 w-3.5" />
                        {t("alerts.form.channel.on")}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={togglePush}
                        disabled={pushBusy}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-60"
                      >
                        {pushBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("alerts.form.channel.enable")}
                      </button>
                    )}
                  </div>
                )}
                <div className="flex items-center gap-3 py-2.5">
                  <Send className="h-4 w-4 shrink-0 text-gray-400" />
                  <span className="min-w-0 flex-1 text-sm text-gray-800">Telegram</span>
                  {telegramConnected ? (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">
                      <Check className="h-3.5 w-3.5" />
                      {t("alerts.form.channel.on")}
                    </span>
                  ) : telegramLink ? (
                    <a
                      href={telegramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300"
                    >
                      {t("alerts.form.channel.connect")}
                    </a>
                  ) : (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />
                  )}
                </div>
                {pushErr && <p className="pt-2 text-xs font-medium text-rose-600">{pushErr}</p>}
              </div>
            )}
          </div>

          {/* The alert's name: optional, nothing suggested, so an empty
              box is the norm and a typed one is kept as is (owner,
              2026-10-11). */}
          <div className="rounded-2xl border border-gray-200 bg-white p-4">
            <SectionLabel>{t("alerts.form.name")}</SectionLabel>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              maxLength={120}
              autoComplete="off"
              className="h-11 w-full rounded-xl border border-gray-200 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
            <p className="mt-1.5 text-xs text-gray-500">{t("alerts.form.name.hint")}</p>
          </div>

          <div className="hidden space-y-3 sm:block">
            {/* "New listings only": a disclaimer, not a callout (owner,
                2026-09-20: the indigo sparkle card read as an AI feature).
                The alert watches listings posted from now on; a seller
                re-posting the same flat does not ping twice. */}
            <p className="text-[11px] leading-relaxed text-gray-400">
              {t("alerts.form.newonly.note")}
            </p>
            {/* Skip the reason when it's the too-broad message: the
                EstimatePanel directly above already shows the identical
                warning, and the two stacked read as a rendering bug. */}
            {blockReason && !tooBroad && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-medium text-amber-900">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{blockReason}</span>
              </div>
            )}
            {/* Anonymous: say up front that Save leads to the (free)
                signup and that nothing they configured is lost - the
                redirect must never be a surprise. */}
            {!user && !blockReason && (
              <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs font-medium text-gray-700">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span>{t("alerts.form.anon.note")}</span>
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <Link
                href="/alerts"
                className="px-4 py-3 text-sm font-medium text-gray-600 hover:text-gray-900 dark:hover:text-white"
              >
                {t("alerts.form.cancel")}
              </Link>
              <button
                type="submit"
                disabled={blocked}
                title={blockReason ?? undefined}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                <Bell className="h-4 w-4" />
                {saveLabel}
              </button>
            </div>
          </div>

          {/* Mobile: spacer so the last fields scroll clear of the fixed
              action bar (which sits above the bottom tab bar). */}
          <div className="h-28 sm:hidden" aria-hidden />
        </form>

        {/* Outside <form>: Enter in one of its fields must not save. */}
        {panelLoaded && (
          <ListingsFilterPanel
            open={panelOpen}
            onClose={() => setPanelOpen(false)}
            searchParams={browseNow as unknown as ReadonlyURLSearchParams}
            onSearch={(params) => { applyPanel(params); setPanelOpen(false); }}
            onReset={() => applyPanel({})}
            showKind
            forAlert
          />
        )}

        {/* Mobile: fixed action bar pinned just above the bottom tab bar
            (4rem + safe area). Carries the Save button and the "why you
            can't create it yet" reason. Lives OUTSIDE <form> but submits
            it via form="..."; kept here so it overlays the whole page. */}
        <div className="fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-[1090] border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:hidden">
          {blockReason && (
            <p className="mb-2 flex items-start gap-1.5 text-[11px] font-medium text-amber-700 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span className="line-clamp-2">{blockReason}</span>
            </p>
          )}
          {!user && !blockReason && (
            <p className="mb-2 flex items-start gap-1.5 text-[11px] font-medium text-gray-600">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
              <span className="line-clamp-2">{t("alerts.form.anon.note")}</span>
            </p>
          )}
          <button
            type="submit"
            form="new-alert-form"
            disabled={blocked}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-indigo-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            <Bell className="h-4 w-4" />
            {saveLabel}
          </button>
        </div>
      </main>
    </div>
  );
}

/** Browser-push opt-in for THIS device - an alternative delivery channel
 *  to Telegram. Presentational: the form owns the eligibility +
 *  subscription state (so the save gate can read it) and the toggle
 *  handler. Renders nothing on ineligible browsers (iOS Safari tab, or no
 *  Push API), per the "show it only to eligible users" rule. */
function PushChannelCard({
  t,
  inApp,
  eligible,
  subscribed,
  busy,
  err,
  onToggle,
}: {
  t: ReturnType<typeof useT>;
  inApp: boolean;
  eligible: boolean;
  subscribed: boolean;
  busy: boolean;
  err: string | null;
  onToggle: () => void;
}) {
  if (!eligible) return null;
  const titleKey = inApp ? "alerts.form.push.app" : "alerts.form.push";
  const helpKey = inApp ? "alerts.form.push.app.help" : "alerts.form.push.help";

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100 text-gray-600 shrink-0">
            <BellRing className="w-5 h-5" />
          </span>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {t(titleKey as Parameters<typeof t>[0])}
            </h3>
            <p className="mt-0.5 text-xs leading-relaxed text-gray-500">
              {t(helpKey as Parameters<typeof t>[0])}
            </p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={subscribed}
          aria-label={t(titleKey as Parameters<typeof t>[0])}
          onClick={onToggle}
          disabled={busy}
          className={`relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-60 ${
            subscribed ? "bg-indigo-600" : "bg-gray-300 dark:bg-white/20"
          }`}
        >
          {busy ? (
            <Loader2 className="absolute inset-0 m-auto h-3.5 w-3.5 animate-spin text-white" />
          ) : (
            <span
              className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                subscribed ? "left-[22px]" : "left-0.5"
              }`}
            />
          )}
        </button>
      </div>
      {err && (
        <p className="mt-2.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          {err}
        </p>
      )}
    </div>
  );
}

/** Live "how busy will this alert be" panel. Shows the estimated new
 *  listings/week, and a warning + (the submit button does the actual
 *  gating) when the filter is too broad. */
// Reserve the panel's height in every state: the estimate arrives ~1 s
// after load (debounced fetch, no user input in the 500 ms window), so a
// null-to-box swap counted as layout shift for everything below it.
function EstimatePanel(props: {
  estimate: Estimate | null;
  estimating: boolean;
  t: ReturnType<typeof useT>;
}) {
  return <div className="min-h-16"><EstimatePanelInner {...props} /></div>;
}

function EstimatePanelInner({
  estimate,
  estimating,
  t,
}: {
  estimate: Estimate | null;
  estimating: boolean;
  t: ReturnType<typeof useT>;
}) {
  if (estimating && !estimate) {
    return (
      <div className="flex items-center gap-2 px-3.5 py-3 rounded-xl border border-gray-200 bg-gray-50 text-xs text-gray-500">
        <Loader2 className="w-3.5 h-3.5 animate-spin" />
        {t("alerts.form.estimate.loading")}
      </div>
    );
  }
  if (!estimate) return null;

  const tooBroad = estimate.too_broad;
  const weekly = estimate.weekly_estimate;
  // Colours use the app's auto-dark convention (bg-*-50 + text-*-900 are
  // remapped to dark-bg / light-text by globals.css). Avoid opacity
  // variants like bg-amber-50/70 - those compile to a different class the
  // remap doesn't match, so the box went near-invisible in dark mode.
  const box = tooBroad
    ? "border-amber-200 bg-amber-50"
    : "border-emerald-200 bg-emerald-50";
  const text = tooBroad ? "text-amber-900" : "text-emerald-900";
  return (
    <div className={`px-3.5 py-3 rounded-xl border ${box}`}>
      <div className="flex items-start gap-2.5">
        {tooBroad ? (
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
        ) : (
          <Bell className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
        )}
        <div className="min-w-0">
          <p className={`text-sm font-semibold ${text}`}>
            {t("alerts.form.estimate.headline").replace("{n}", String(weekly))}
          </p>
          <p className={`mt-0.5 text-xs leading-relaxed ${text}`}>
            {tooBroad
              ? t("alerts.form.estimate.too_broad")
              : t("alerts.form.estimate.ok")}
          </p>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      {children}
    </div>
  );
}

/** Just the uppercase field label - used directly inside the carded
 *  primary-filter rows (which provide their own padding), and reused by
 *  Section for the standalone fields. */
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-semibold text-gray-500 mb-2">
      {children}
    </label>
  );
}

function NumberInput({
  value,
  onChange,
  placeholder,
}: {
  value: number | "";
  onChange: (v: number | "") => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      inputMode="numeric"
      value={value === "" ? "" : groupDigits(value)}
      onChange={(e) => {
        const n = parseGrouped(e.target.value);
        onChange(n === undefined ? "" : n);
      }}
      placeholder={placeholder}
      className="w-full px-3.5 py-3 border border-gray-200 bg-white text-gray-900 rounded-xl text-base focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900/40 outline-none transition-colors"
    />
  );
}

/** Same look as NumberInput, but keeps the raw string value (parsed to a
 *  number only when the filters blob is built) - so a decimal like "8.5"
 *  survives typing. `decimal` switches the mobile keypad; `className`
 *  extends the base classes (e.g. right padding for a unit suffix). */
function RawNumberInput({
  value,
  onChange,
  placeholder,
  decimal = false,
  className = "",
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  decimal?: boolean;
  className?: string;
}) {
  return (
    <input
      type="number"
      inputMode={decimal ? "decimal" : "numeric"}
      min={0}
      step={decimal ? "0.1" : "1"}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3.5 py-3 border border-gray-200 bg-white text-gray-900 rounded-xl text-base focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-900/40 outline-none transition-colors ${className}`}
    />
  );
}

/** The one choice-button style of the form (the calculator form's picker). */
function choiceBtn(on: boolean): string {
  return `h-9 min-w-0 flex-1 truncate rounded-lg border px-1 text-xs font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
    on ? SELECTED : UNSELECTED
  }`;
}

function Tristate({
  value,
  onChange,
  labels,
}: {
  value: "any" | "yes" | "no";
  onChange: (v: "any" | "yes" | "no") => void;
  labels: { any: string; yes: string; no: string };
}) {
  return (
    <div className="flex gap-1.5">
      {(["any", "yes", "no"] as const).map((opt) => (
        <button key={opt} type="button" aria-pressed={value === opt} onClick={() => onChange(opt)} className={choiceBtn(value === opt)}>
          {labels[opt]}
        </button>
      ))}
    </div>
  );
}

/** Suspense boundary: the form reads useSearchParams (?edit=) which
 *  requires a boundary in a Next 15 client page. */
export default function NewAlertPage() {
  return (
    <Suspense fallback={null}>
      <NewAlertForm />
    </Suspense>
  );
}


/** Saved-blob keys the form edits itself; every OTHER key is carried
 *  through as-is, so a filter the form has no control for is never lost. */
const FORM_KEYS = new Set([
  "room_counts", "listing_kind", "price_min", "price_max", "area_min", "area_max",
  "min_yield", "photo_conditions", "feature_tags", "q",
  "seller_type", "floor_min", "floor_max", "leased", "geo_mode", "location_ids", "complex_slugs",
  "center_lat", "center_lng", "radius_km", "polygon", "has_repair", "has_mortgage", "has_kupcha",
  "exclude_first_floor", "exclude_last_floor",
  "mortgage_kind", "max_initial", "mortgage_rate_min", "mortgage_rate_max", "mortgage_terms_known", "mortgage_years",
  // Not filters: the alert's own settings, and legacy name-based places.
  "min_verdict_tier", "notify_frequency", "location_names", "complex_names",
]);

/** Kept filters the search bar already shows as its own chips. The
 *  number of floors is one (its storeys chip), set in the listings filter
 *  sheet (ticket 63). */
const BAR_SHOWN = new Set(["rent_period", "metro_walk", "total_floors_min", "total_floors_max"]);

function pickExtra(f: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(f)) {
    if (FORM_KEYS.has(k)) continue;
    const v = f[k];
    if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) continue;
    out[k] = v;
  }
  return out;
}

function extraLabel(
  k: string,
  v: unknown,
  t: ReturnType<typeof useT>,
  vocab: FeatureTagVocab | null,
  locale: string,
): string {
  const tag = (id: string) => {
    const d = vocab?.tags.find((x) => x.id === id);
    return d ? d.labels[locale as "az" | "ru" | "en"] || d.labels.az : id;
  };
  const list = Array.isArray(v) ? v.map(String) : [String(v)];
  switch (k) {
    case "deal_tiers":
      return `${t("listings.filter.deal_tier")}: ${list.map((x) => t(`listings.filter.deal_tier.${x}` as Parameters<typeof t>[0])).join(", ")}`;
    case "rent_period":
      return `${t("listings.filter.rent_period")}: ${t(`listings.filter.rent_period.${list[0]}` as Parameters<typeof t>[0])}`;
    case "metro_walk":
      return `${t("listings.filter.metro_walk")}: ${t("listings.filter.metro_walk.min").replace("{n}", list[0])}`;
    case "exclude_tags":
      return `${t("alerts.form.extra.excluded")}: ${list.map(tag).join(", ")}`;
    case "category":
      return (["house", "land", "commercial", "garage"] as const).includes(list[0] as "house")
        ? t(`listings.filter.category.${list[0] as "house" | "land" | "commercial" | "garage"}`)
        : t("listings.filter.category.apartment");
    case "property_kinds":
      return `${t("listings.filter.property_kind")}: ${list.map((x) => t(kindChipKey(x))).join(", ")}`;
    case "building_type":
      return t(list[0] === "old" ? "listings.filter.building_type.old" : "listings.filter.building_type.new");
    case "ppm_min":
      return `${t("alerts.form.extra.ppm")} ≥ ${groupDigits(list[0])}`;
    case "ppm_max":
      return `${t("alerts.form.extra.ppm")} ≤ ${groupDigits(list[0])}`;
    case "seller":
      return t("alerts.form.extra.seller");
    case "listed_after":
      return `${t("listings.filter.listed")}: ${t("listings.filter.listed.from")} ${list[0]}`;
    case "listed_before":
      return `${t("listings.filter.listed")}: ${t("listings.filter.listed.to")} ${list[0]}`;
    case "district":
    case "districts":
    case "metros":
    case "sub_locations":
    case "street_names":
    case "cities":
      return list.join(", ");
    // Places and towns the search ruled out ("Xırdalan olmasın").
    case "exclude_cities":
      return `${t("alerts.form.extra.excluded")}: ${list.join(", ")}`;
    case "exclude_location_ids":
      return `${t("alerts.form.extra.excluded")} · ${list.length}`;
    case "street":
      return `${t("listings.magic.chip.street")} · ${list.length}`;
    // Set in the listings filter sheet (no control of the form's own).
    case "source":
      return `${t("listings.filter.source")}: ${list.map((x) => x.replace(/_az$/, ".az")).join(", ")}`;
    case "developers":
      return `${t("listings.filter.developer")} · ${list.length}`;
    case "sot_min":
      return `${t("listings.filter.sot")} ≥ ${list[0]}`;
    case "sot_max":
      return `${t("listings.filter.sot")} ≤ ${list[0]}`;
    case "pps_min":
      return `${t("listings.filter.pps")} ≥ ${groupDigits(list[0])}`;
    case "pps_max":
      return `${t("listings.filter.pps")} ≤ ${groupDigits(list[0])}`;
    default:
      return `${k}: ${list.join(", ")}`;
  }
}

/** Fields every alert filter starts from (the search bar replaces, it
 *  does not merge into, the current picks). */
const EMPTY_FILTERS = {
  roomCounts: [], listingKind: [], priceMin: "", priceMax: "", areaMin: "", areaMax: "",
  minYield: "", photoConditions: [], featureTags: [],
  q: "", sellerType: "any", floorMin: "", floorMax: "", geoMode: "area", locationIds: [],
  complexSlugs: [], centerLat: null, centerLng: null, radiusKm: 2, polygon: [], hasRepair: "any",
  hasMortgage: "any", mortgageKind: "", maxInitial: "", mortgageRateMin: "", mortgageRateMax: "", mortgageTermsKnown: "any", mortgageYears: [],
  hasKupcha: "any", excludeFirstFloor: false, excludeLastFloor: false,
} as const;

/** The geo tab a saved blob opens on. "draw" is stored since the tab
 *  exists; an alert saved from a browse URL with a drawn area carries
 *  the rings under "area" (or no mode) and opens on the draw tab too,
 *  the rings being the narrower of the two. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function geoModeOf(f: Record<string, any>): GeoMode {
  if (f.geo_mode === "radius") return "radius";
  if (f.geo_mode === "draw") return "draw";
  if (Array.isArray(f.polygon) && f.polygon.length > 0) return "draw";
  return "area";
}

/** Saved-blob shape -> form state. ``replace`` clears every filter the
 *  blob does not set (search bar); otherwise absent keys keep ``prev``
 *  (the listings prefill only adds). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
/** The take-over block of a saved blob or a browse URL (listings panel
 *  parity; owner, 2026-10-16: "I don't think that I can select the
 *  mortgage" in the alert form). A key the blob lacks keeps ``b``'s value. */
function mortgageFromBlob(
  f: Record<string, unknown>,
  b: FormState,
): Pick<FormState, "mortgageKind" | "maxInitial" | "mortgageRateMin" | "mortgageRateMax" | "mortgageTermsKnown" | "mortgageYears"> {
  const str = (v: unknown): string | null => (v != null && v !== "" ? String(v) : null);
  const tri = (v: unknown): Tristate | null => (v === "yes" || v === "no" ? v : null);
  return {
    mortgageKind: f.mortgage_kind === "takeover" ? "takeover" : b.mortgageKind,
    maxInitial: str(f.max_initial) ?? b.maxInitial,
    mortgageRateMin: str(f.mortgage_rate_min) ?? b.mortgageRateMin,
    mortgageRateMax: str(f.mortgage_rate_max) ?? b.mortgageRateMax,
    mortgageTermsKnown: tri(f.mortgage_terms_known) ?? b.mortgageTermsKnown,
    mortgageYears: Array.isArray(f.mortgage_years) ? f.mortgage_years.map(String) : b.mortgageYears,
  };
}

function blobToForm(prev: FormState, f: Record<string, any>, replace: boolean): FormState {
  const b = (replace ? { ...prev, ...EMPTY_FILTERS } : prev) as FormState;
  return {
    ...b,
      roomCounts: Array.isArray(f.room_counts) ? f.room_counts : b.roomCounts,
      listingKind: Array.isArray(f.listing_kind) ? f.listing_kind : b.listingKind,
      priceMin: f.price_min ?? b.priceMin,
      priceMax: f.price_max ?? b.priceMax,
      areaMin: f.area_min ?? b.areaMin,
      areaMax: f.area_max ?? b.areaMax,
      minYield: f.min_yield != null ? String(f.min_yield) : b.minYield,
      photoConditions: Array.isArray(f.photo_conditions)
        ? f.photo_conditions.map(String)
        : b.photoConditions,
      featureTags: Array.isArray(f.feature_tags) ? f.feature_tags.map(String) : b.featureTags,
      q: typeof f.q === "string" ? f.q : b.q,
      geoMode:
        f.geo_mode === "radius" || f.geo_mode === "draw" || (Array.isArray(f.polygon) && f.polygon.length > 0)
          ? geoModeOf(f)
          : b.geoMode,
      locationIds: Array.isArray(f.location_ids) ? f.location_ids.map(String) : b.locationIds,
      complexSlugs: Array.isArray(f.complex_slugs) ? f.complex_slugs.map(String) : b.complexSlugs,
      sellerType: (["owner", "agent", "company"] as const).includes(f.seller_type) ? f.seller_type : b.sellerType,
      floorMin: f.floor_min != null ? String(f.floor_min) : b.floorMin,
      floorMax: f.floor_max != null ? String(f.floor_max) : b.floorMax,
      leased: f.leased === true ? "rent" : f.leased === false ? "sale" : "all",
      centerLat: f.center_lat ?? b.centerLat,
      centerLng: f.center_lng ?? b.centerLng,
      radiusKm: f.radius_km ?? b.radiusKm,
      polygon: Array.isArray(f.polygon) ? polygonParamsToRings(f.polygon.map(String)) : b.polygon,
      hasRepair: f.has_repair === "yes" || f.has_repair === "no" ? f.has_repair : b.hasRepair,
      hasMortgage: f.has_mortgage === "yes" || f.has_mortgage === "no" ? f.has_mortgage : b.hasMortgage,
      ...mortgageFromBlob(f, b),
      hasKupcha: f.has_kupcha === "yes" || f.has_kupcha === "no" ? f.has_kupcha : b.hasKupcha,
      excludeFirstFloor: f.exclude_first_floor === true || b.excludeFirstFloor,
      excludeLastFloor: f.exclude_last_floor === true || b.excludeLastFloor,
      // Anonymous drafts round-trip through login as a prefill blob that
      // also carries the non-filter picks - restore them too.
      minVerdictTier: (
        ["any", "good_deal", "great_deal", "exceptional_deal"].includes(f.min_verdict_tier)
          ? f.min_verdict_tier
          : b.minVerdictTier
      ) as Tier,
      notifyFrequency: (
        (NOTIFY_FREQUENCIES as readonly string[]).includes(f.notify_frequency)
          ? f.notify_frequency
          : b.notifyFrequency
      ) as NotifyFrequency,
  };
}
